/**
 * PATH: packages/ui/src/kanban/components/comment-text/linkify.ts
 * FILE_ID: UI-KANBAN-COMMENT-TEXT-001
 * PURPOSE: Pure text→segment model for rendering URLs inside comment/note
 *   bodies as recognisable, clickable chips (Google Sheet, OneDrive, …).
 * WHY: FEAT-2026-07-06-KANBAN-COMMENT-LINKS — operators paste external file
 *   links (Google Sheets etc.) into card notes; raw 200-char URLs are
 *   unreadable and were not clickable. Trailing punctuation is deliberately
 *   excluded from the URL (a pasted "see https://x.com/y." must not 404).
 */

export interface TextSegment {
  kind: 'text'
  text: string
}

export interface LinkSegment {
  kind: 'link'
  /** The full original URL (href + hover title). */
  url: string
  /** Short human label, e.g. "Google Sheet" or "acme.com". */
  label: string
}

export type CommentSegment = TextSegment | LinkSegment

// http(s) URLs only — bare "www." or naked domains are ambiguous in prose
// and mailto/tel already render natively where needed.
// `)` IS allowed inside the match (wikipedia-style URLs); an unbalanced
// trailing `)` is stripped after the fact in splitLinkSegments.
const URL_RE = /https?:\/\/[^\s<>"'\]]+/g

// Punctuation that reads as sentence-trailing, not URL-trailing.
const TRAILING_PUNCT_RE = /[.,;:!?…]+$/

/**
 * Known external file/tool providers → short-form label. Checked in order;
 * first match wins. Path checks run against `hostname + pathname`.
 */
const PROVIDERS: ReadonlyArray<{ match: RegExp; label: string }> = [
  { match: /docs\.google\.com\/spreadsheets/, label: 'Google Sheet' },
  { match: /docs\.google\.com\/document/, label: 'Google Doc' },
  { match: /docs\.google\.com\/presentation/, label: 'Google Slides' },
  { match: /docs\.google\.com\/forms/, label: 'Google Form' },
  { match: /drive\.google\.com/, label: 'Google Drive file' },
  { match: /(^|\.)sharepoint\.com/, label: 'SharePoint file' },
  { match: /1drv\.ms|onedrive\.live\.com/, label: 'OneDrive file' },
  { match: /(^|\.)dropbox\.com/, label: 'Dropbox file' },
  { match: /(^|\.)notion\.so|notion\.site/, label: 'Notion page' },
  { match: /(^|\.)figma\.com/, label: 'Figma file' },
  { match: /(^|\.)trello\.com/, label: 'Trello board' },
  { match: /(^|\.)airtable\.com/, label: 'Airtable base' },
  { match: /(^|\.)canva\.com/, label: 'Canva design' },
  { match: /(^|\.)loom\.com/, label: 'Loom video' },
  { match: /(^|\.)youtube\.com|youtu\.be/, label: 'YouTube video' },
  { match: /(^|\.)docusign\.(com|net)/, label: 'DocuSign' },
  { match: /(^|\.)xero\.com/, label: 'Xero' },
]

/** Short-form label for a URL: known provider name, else the bare domain. */
export function describeLink(url: string): string {
  let host = ''
  let path = ''
  try {
    const u = new URL(url)
    host = u.hostname.toLowerCase()
    path = u.pathname
  } catch {
    return url
  }
  const target = host + path
  for (const p of PROVIDERS) {
    if (p.match.test(target)) return p.label
  }
  return host.replace(/^www\./, '')
}

/**
 * Split free text into text/link segments. Trailing sentence punctuation
 * and an unbalanced closing paren are pushed back into the text segment.
 */
export function splitLinkSegments(text: string): CommentSegment[] {
  if (!text) return []
  const segments: CommentSegment[] = []
  let last = 0
  for (const m of text.matchAll(URL_RE)) {
    let url = m[0]
    const start = m.index ?? 0
    // Strip trailing punctuation, then a trailing ')' with no matching '('
    // inside the URL (the "(see https://x.com)" paste shape).
    url = url.replace(TRAILING_PUNCT_RE, '')
    while (
      url.endsWith(')') &&
      (url.match(/\(/g)?.length ?? 0) < (url.match(/\)/g)?.length ?? 0)
    ) {
      url = url.slice(0, -1)
    }
    if (start > last) segments.push({ kind: 'text', text: text.slice(last, start) })
    segments.push({ kind: 'link', url, label: describeLink(url) })
    last = start + url.length
  }
  if (last < text.length) segments.push({ kind: 'text', text: text.slice(last) })
  return segments
}

/** True when the text contains at least one http(s) URL. */
export function containsLink(text: string): boolean {
  URL_RE.lastIndex = 0
  return URL_RE.test(text)
}
